import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { useStore } from "../context/StoreContext";
import { api } from "../lib/api";
import { eur, packTotal } from "../lib/format";
import { getUtm, track } from "../lib/analytics";
import { PawPrint, Lock, ArrowLeft, Truck, ShieldCheck, Loader2 } from "lucide-react";

const FIELD = "w-full rounded-xl border border-zinc-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40 bg-white";

function PaymentForm({ clientSecret, total, orderPayload }) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState("");

  const pay = async () => {
    if (!stripe || !elements) return;
    setPaying(true);
    setError("");
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: "if_required",
      confirmParams: {
        return_url: `${window.location.origin}/pago/exito`,
        payment_method_data: {
          billing_details: {
            email: orderPayload.customer.email,
            name: orderPayload.customer.name,
            phone: orderPayload.customer.phone,
            address: orderPayload.customer.address,
          },
        },
      },
    });
    if (error) {
      setError(error.message || "Payment could not be completed. Please try again.");
      setPaying(false);
      return;
    }
    if (paymentIntent && paymentIntent.status === "succeeded") {
      navigate(`/pago/exito?payment_intent=${paymentIntent.id}`);
    } else if (paymentIntent) {
      navigate(`/pago/exito?payment_intent=${paymentIntent.id}`);
    }
  };

  return (
    <div>
      <PaymentElement options={{ layout: "tabs" }} />
      {error && <p data-testid="payment-error" className="mt-3 text-sm text-red-600">{error}</p>}
      <button
        data-testid="pay-button"
        onClick={pay}
        disabled={paying || !stripe}
        className="mt-5 w-full bg-brand hover:bg-brand-dark disabled:opacity-60 text-white font-display font-bold text-base rounded-full py-4 shadow-lg shadow-brand/30 transition-colors flex items-center justify-center gap-2"
      >
        {paying ? <Loader2 className="w-5 h-5 animate-spin" /> : <Lock className="w-4 h-4" />}
        {paying ? "Processing payment…" : `PAY ${eur(total)}`}
      </button>
      <p className="mt-3 text-center text-xs text-zinc-400 flex items-center justify-center gap-1.5">
        <ShieldCheck className="w-3.5 h-3.5" /> Payments are encrypted and processed by Stripe. We never store your card details.
      </p>
    </div>
  );
}

export default function CheckoutPage() {
  const { config, selectedPack } = useStore();
  const navigate = useNavigate();
  const fired = useRef(false);
  const [step, setStep] = useState(1);
  const [creating, setCreating] = useState(false);
  const [formError, setFormError] = useState("");
  const [intent, setIntent] = useState(null);
  const [form, setForm] = useState({
    email: "", phone: "", name: "", line1: "", line2: "", city: "", postal_code: "", province: "",
  });

  const stripePromise = useMemo(
    () => (config?.stripe_publishable_key ? loadStripe(config.stripe_publishable_key) : null),
    [config]
  );

  useEffect(() => {
    if (config && selectedPack && !fired.current) {
      fired.current = true;
      track("InitiateCheckout", {
        value: packTotal(selectedPack),
        currency: "USD",
        content_ids: ["pelocero"],
        contents: [{ id: "pelocero", quantity: selectedPack.units, price: Number(selectedPack.price) }],
        num_items: selectedPack.units,
      });
    }
  }, [config, selectedPack]);

  if (!config || !selectedPack) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 rounded-full border-2 border-brand border-t-transparent animate-spin" />
      </div>
    );
  }

  const total = packTotal(selectedPack);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const continueToPayment = async () => {
    setFormError("");
    const missing = [];
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) missing.push("a valid email");
    if (!form.name.trim()) missing.push("full name");
    if (!form.line1.trim()) missing.push("street address");
    if (!form.city.trim()) missing.push("city");
    if (!/^\d{5}$/.test(form.postal_code.trim())) missing.push("ZIP code (5 digits)");
    if (missing.length) {
      setFormError(`Please check: ${missing.join(", ")}.`);
      return;
    }
    setCreating(true);
    try {
      const { data } = await api.post("/payments/create-intent", {
        pack_id: selectedPack.id,
        utm: getUtm(),
        customer: {
          email: form.email,
          name: form.name,
          phone: form.phone,
          address: {
            line1: form.line1,
            line2: form.line2,
            city: form.city,
            postal_code: form.postal_code,
            state: form.province,
            country: "US",
          },
        },
      });
      setIntent(data);
      setStep(2);
      window.scrollTo(0, 0);
    } catch (e) {
      setFormError("We couldn't prepare your payment. Please try again.");
    }
    setCreating(false);
  };

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="checkout-page">
      <header className="bg-white border-b border-zinc-100">
        <div className="max-w-5xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="checkout-logo">
            <span className="w-8 h-8 rounded-full bg-brand flex items-center justify-center">
              <PawPrint className="w-4 h-4 text-white" />
            </span>
            <span className="font-display font-bold text-lg">PeloCero</span>
          </Link>
          <span className="text-xs text-zinc-400 flex items-center gap-1.5">
            <Lock className="w-3.5 h-3.5" /> Secure SSL payment
          </span>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8 sm:py-12 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-7 order-2 lg:order-1">
          <button
            data-testid="checkout-back-button"
            onClick={() => (step === 2 ? setStep(1) : navigate("/"))}
            className="text-sm text-zinc-500 hover:text-ink flex items-center gap-1.5 mb-6"
          >
            <ArrowLeft className="w-4 h-4" /> {step === 2 ? "Back to my details" : "Back to the store"}
          </button>

          {step === 1 && (
            <div className="space-y-8" data-testid="checkout-form">
              <section>
                <h2 className="font-display font-bold text-lg mb-4">1 · Contact</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input data-testid="checkout-email" type="email" placeholder="Email *" value={form.email} onChange={set("email")} className={FIELD} />
                  <input data-testid="checkout-phone" type="tel" placeholder="Phone (for the carrier)" value={form.phone} onChange={set("phone")} className={FIELD} />
                </div>
              </section>
              <section>
                <h2 className="font-display font-bold text-lg mb-4">2 · Shipping address</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input data-testid="checkout-name" placeholder="Full name *" value={form.name} onChange={set("name")} className={`${FIELD} sm:col-span-2`} />
                  <input data-testid="checkout-address" placeholder="Street address *" value={form.line1} onChange={set("line1")} className={`${FIELD} sm:col-span-2`} />
                  <input data-testid="checkout-address2" placeholder="Apt, suite… (optional)" value={form.line2} onChange={set("line2")} className={`${FIELD} sm:col-span-2`} />
                  <input data-testid="checkout-city" placeholder="City *" value={form.city} onChange={set("city")} className={FIELD} />
                  <input data-testid="checkout-zip" placeholder="ZIP code *" value={form.postal_code} onChange={set("postal_code")} className={FIELD} />
                  <input data-testid="checkout-province" placeholder="State" value={form.province} onChange={set("province")} className={FIELD} />
                  <div className={`${FIELD} text-zinc-500 flex items-center`}>United States</div>
                </div>
              </section>
              {formError && <p data-testid="checkout-form-error" className="text-sm text-red-600">{formError}</p>}
              <button
                data-testid="checkout-continue-button"
                onClick={continueToPayment}
                disabled={creating}
                className="w-full bg-ink hover:bg-zinc-800 disabled:opacity-60 text-white font-display font-bold text-base rounded-full py-4 transition-colors flex items-center justify-center gap-2"
              >
                {creating && <Loader2 className="w-5 h-5 animate-spin" />}
                {creating ? "Preparing secure payment…" : "CONTINUE TO PAYMENT"}
              </button>
            </div>
          )}

          {step === 2 && intent && (
            <section data-testid="payment-step">
              <h2 className="font-display font-bold text-lg mb-1">3 · Payment</h2>
              <p className="text-sm text-zinc-500 mb-5">
                Shipping to <strong className="text-ink">{form.name}</strong> · {form.line1}{form.line2 ? `, ${form.line2}` : ""}, {form.city}{form.province ? `, ${form.province}` : ""} {form.postal_code}
              </p>
              {stripePromise ? (
                <Elements
                  stripe={stripePromise}
                  options={{
                    clientSecret: intent.client_secret,
                    locale: "en",
                    appearance: {
                      theme: "stripe",
                      variables: {
                        colorPrimary: "#FF4D2D",
                        borderRadius: "12px",
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                      },
                    },
                  }}
                >
                  <PaymentForm
                    clientSecret={intent.client_secret}
                    total={total}
                    orderPayload={{ customer: { email: form.email, name: form.name, phone: form.phone, address: { line1: form.line1, line2: form.line2, city: form.city, postal_code: form.postal_code, state: form.province, country: "US" } } }}
                  />
                </Elements>
              ) : (
                <p className="text-sm text-red-600">Stripe is not configured.</p>
              )}
            </section>
          )}
        </div>

        <aside className="lg:col-span-5 order-1 lg:order-2">
          <div className="bg-white rounded-3xl ring-1 ring-zinc-200 p-6 lg:sticky lg:top-8" data-testid="checkout-summary">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-zinc-50 ring-1 ring-zinc-100 overflow-hidden shrink-0">
                {config.media?.hero_image && <img src={config.media.hero_image} alt="PeloCero" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1">
                <p className="font-display font-semibold text-sm">{selectedPack.name}</p>
                {selectedPack.badge && <p className="text-[10px] font-bold text-brand">{selectedPack.badge}</p>}
              </div>
              <p className="font-display font-bold">{eur(selectedPack.price)}</p>
            </div>
            <div className="mt-5 pt-5 border-t border-zinc-100 space-y-2.5 text-sm">
              <div className="flex justify-between text-zinc-500">
                <span>Subtotal</span>
                <span>{eur(selectedPack.price)}</span>
              </div>
              <div className="flex justify-between text-zinc-500">
                <span className="flex items-center gap-1.5"><Truck className="w-4 h-4" /> Shipping</span>
                <span className={selectedPack.free_shipping ? "text-green-600 font-semibold" : ""}>
                  {selectedPack.free_shipping ? "FREE" : eur(selectedPack.shipping)}
                </span>
              </div>
              <div className="flex justify-between font-display font-bold text-lg pt-3 border-t border-zinc-100">
                <span>Total</span>
                <span data-testid="checkout-total">{eur(total)}</span>
              </div>
              <p className="text-[11px] text-zinc-400">Prices in USD</p>
            </div>
          </div>
        </aside>
      </main>
    </div>
  );
}
